/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */ const $0e112480d1414235$export$be5234c0b764b6e0 = Symbol("Comlink.proxy");
const $0e112480d1414235$export$39fb953702b7fcd7 = Symbol("Comlink.endpoint");
const $0e112480d1414235$export$89981d4b8d9f48aa = Symbol("Comlink.releaseProxy");
const $0e112480d1414235$export$ddc31dfe7c269837 = Symbol("Comlink.finalizer");
const $0e112480d1414235$var$throwMarker = Symbol("Comlink.thrown");
const $0e112480d1414235$var$isObject = (val)=>typeof val === "object" && val !== null || typeof val === "function";
/**
 * Internal transfer handle to handle objects marked to proxy.
 */ const $0e112480d1414235$var$proxyTransferHandler = {
    canHandle: (val)=>$0e112480d1414235$var$isObject(val) && val[$0e112480d1414235$export$be5234c0b764b6e0],
    serialize (obj) {
        const { port1: port1, port2: port2 } = new MessageChannel();
        $0e112480d1414235$export$9b7f6e342a8cbd38(obj, port1);
        return [
            port2,
            [
                port2
            ]
        ];
    },
    deserialize (port) {
        port.start();
        return $0e112480d1414235$export$4997ffc0176396a6(port);
    }
};
/**
 * Internal transfer handler to handle thrown exceptions.
 */ const $0e112480d1414235$var$throwTransferHandler = {
    canHandle: (value)=>$0e112480d1414235$var$isObject(value) && $0e112480d1414235$var$throwMarker in value,
    serialize ({ value: value }) {
        let serialized;
        if (value instanceof Error) serialized = {
            isError: true,
            value: {
                message: value.message,
                name: value.name,
                stack: value.stack
            }
        };
        else serialized = {
            isError: false,
            value: value
        };
        return [
            serialized,
            []
        ];
    },
    deserialize (serialized) {
        if (serialized.isError) throw Object.assign(new Error(serialized.value.message), serialized.value);
        throw serialized.value;
    }
};
/**
 * Allows customizing the serialization of certain values.
 */ const $0e112480d1414235$export$ab8f1c00731ee83e = new Map([
    [
        "proxy",
        $0e112480d1414235$var$proxyTransferHandler
    ],
    [
        "throw",
        $0e112480d1414235$var$throwTransferHandler
    ]
]);
function $0e112480d1414235$var$isAllowedOrigin(allowedOrigins, origin) {
    for (const allowedOrigin of allowedOrigins){
        if (origin === allowedOrigin || allowedOrigin === "*") return true;
        if (allowedOrigin instanceof RegExp && allowedOrigin.test(origin)) return true;
    }
    return false;
}
function $0e112480d1414235$export$9b7f6e342a8cbd38(obj, ep = globalThis, allowedOrigins = [
    "*"
]) {
    ep.addEventListener("message", function callback(ev) {
        if (!ev || !ev.data) return;
        if (!$0e112480d1414235$var$isAllowedOrigin(allowedOrigins, ev.origin)) {
            console.warn(`Invalid origin '${ev.origin}' for comlink proxy`);
            return;
        }
        const { id: id, type: type, path: path } = Object.assign({
            path: []
        }, ev.data);
        const argumentList = (ev.data.argumentList || []).map($0e112480d1414235$var$fromWireValue);
        let returnValue;
        try {
            const parent = path.slice(0, -1).reduce((obj, prop)=>obj[prop], obj);
            const rawValue = path.reduce((obj, prop)=>obj[prop], obj);
            switch(type){
                case "GET" /* MessageType.GET */ :
                    returnValue = rawValue;
                    break;
                case "SET" /* MessageType.SET */ :
                    parent[path.slice(-1)[0]] = $0e112480d1414235$var$fromWireValue(ev.data.value);
                    returnValue = true;
                    break;
                case "APPLY" /* MessageType.APPLY */ :
                    returnValue = rawValue.apply(parent, argumentList);
                    break;
                case "CONSTRUCT" /* MessageType.CONSTRUCT */ :
                    {
                        const value = new rawValue(...argumentList);
                        returnValue = $0e112480d1414235$export$923f96dd5afa9ce6(value);
                    }
                    break;
                case "ENDPOINT" /* MessageType.ENDPOINT */ :
                    {
                        const { port1: port1, port2: port2 } = new MessageChannel();
                        $0e112480d1414235$export$9b7f6e342a8cbd38(obj, port2);
                        returnValue = $0e112480d1414235$export$c9b234447cf4ef7c(port1, [
                            port1
                        ]);
                    }
                    break;
                case "RELEASE" /* MessageType.RELEASE */ :
                    returnValue = undefined;
                    break;
                default:
                    return;
            }
        } catch (value) {
            returnValue = {
                value: value,
                [$0e112480d1414235$var$throwMarker]: 0
            };
        }
        Promise.resolve(returnValue).catch((value)=>{
            return {
                value: value,
                [$0e112480d1414235$var$throwMarker]: 0
            };
        }).then((returnValue)=>{
            const [wireValue, transferables] = $0e112480d1414235$var$toWireValue(returnValue);
            ep.postMessage(Object.assign(Object.assign({}, wireValue), {
                id: id
            }), transferables);
            if (type === "RELEASE" /* MessageType.RELEASE */ ) {
                // detach and deactive after sending release response above.
                ep.removeEventListener("message", callback);
                $0e112480d1414235$var$closeEndPoint(ep);
                if ($0e112480d1414235$export$ddc31dfe7c269837 in obj && typeof obj[$0e112480d1414235$export$ddc31dfe7c269837] === "function") obj[$0e112480d1414235$export$ddc31dfe7c269837]();
            }
        }).catch((error)=>{
            // Send Serialization Error To Caller
            const [wireValue, transferables] = $0e112480d1414235$var$toWireValue({
                value: new TypeError("Unserializable return value"),
                [$0e112480d1414235$var$throwMarker]: 0
            });
            ep.postMessage(Object.assign(Object.assign({}, wireValue), {
                id: id
            }), transferables);
        });
    });
    if (ep.start) ep.start();
}
function $0e112480d1414235$var$isMessagePort(endpoint) {
    return endpoint.constructor.name === "MessagePort";
}
function $0e112480d1414235$var$closeEndPoint(endpoint) {
    if ($0e112480d1414235$var$isMessagePort(endpoint)) endpoint.close();
}
function $0e112480d1414235$export$4997ffc0176396a6(ep, target) {
    return $0e112480d1414235$var$createProxy(ep, [], target);
}
function $0e112480d1414235$var$throwIfProxyReleased(isReleased) {
    if (isReleased) throw new Error("Proxy has been released and is not useable");
}
function $0e112480d1414235$var$releaseEndpoint(ep) {
    return $0e112480d1414235$var$requestResponseMessage(ep, {
        type: "RELEASE" /* MessageType.RELEASE */ 
    }).then(()=>{
        $0e112480d1414235$var$closeEndPoint(ep);
    });
}
const $0e112480d1414235$var$proxyCounter = new WeakMap();
const $0e112480d1414235$var$proxyFinalizers = "FinalizationRegistry" in globalThis && new FinalizationRegistry((ep)=>{
    const newCount = ($0e112480d1414235$var$proxyCounter.get(ep) || 0) - 1;
    $0e112480d1414235$var$proxyCounter.set(ep, newCount);
    if (newCount === 0) $0e112480d1414235$var$releaseEndpoint(ep);
});
function $0e112480d1414235$var$registerProxy(proxy, ep) {
    const newCount = ($0e112480d1414235$var$proxyCounter.get(ep) || 0) + 1;
    $0e112480d1414235$var$proxyCounter.set(ep, newCount);
    if ($0e112480d1414235$var$proxyFinalizers) $0e112480d1414235$var$proxyFinalizers.register(proxy, ep, proxy);
}
function $0e112480d1414235$var$unregisterProxy(proxy) {
    if ($0e112480d1414235$var$proxyFinalizers) $0e112480d1414235$var$proxyFinalizers.unregister(proxy);
}
function $0e112480d1414235$var$createProxy(ep, path = [], target = function() {}) {
    let isProxyReleased = false;
    const proxy = new Proxy(target, {
        get (_target, prop) {
            $0e112480d1414235$var$throwIfProxyReleased(isProxyReleased);
            if (prop === $0e112480d1414235$export$89981d4b8d9f48aa) return ()=>{
                $0e112480d1414235$var$unregisterProxy(proxy);
                $0e112480d1414235$var$releaseEndpoint(ep);
                isProxyReleased = true;
            };
            if (prop === "then") {
                if (path.length === 0) return {
                    then: ()=>proxy
                };
                const r = $0e112480d1414235$var$requestResponseMessage(ep, {
                    type: "GET" /* MessageType.GET */ ,
                    path: path.map((p)=>p.toString())
                }).then($0e112480d1414235$var$fromWireValue);
                return r.then.bind(r);
            }
            return $0e112480d1414235$var$createProxy(ep, [
                ...path,
                prop
            ]);
        },
        set (_target, prop, rawValue) {
            $0e112480d1414235$var$throwIfProxyReleased(isProxyReleased);
            // FIXME: ES6 Proxy Handler `set` methods are supposed to return a
            // boolean. To show good will, we return true asynchronously ¯\_(ツ)_/¯
            const [value, transferables] = $0e112480d1414235$var$toWireValue(rawValue);
            return $0e112480d1414235$var$requestResponseMessage(ep, {
                type: "SET" /* MessageType.SET */ ,
                path: [
                    ...path,
                    prop
                ].map((p)=>p.toString()),
                value: value
            }, transferables).then($0e112480d1414235$var$fromWireValue);
        },
        apply (_target, _thisArg, rawArgumentList) {
            $0e112480d1414235$var$throwIfProxyReleased(isProxyReleased);
            const last = path[path.length - 1];
            if (last === $0e112480d1414235$export$39fb953702b7fcd7) return $0e112480d1414235$var$requestResponseMessage(ep, {
                type: "ENDPOINT" /* MessageType.ENDPOINT */ 
            }).then($0e112480d1414235$var$fromWireValue);
            // We just pretend that `bind()` didn’t happen.
            if (last === "bind") return $0e112480d1414235$var$createProxy(ep, path.slice(0, -1));
            const [argumentList, transferables] = $0e112480d1414235$var$processArguments(rawArgumentList);
            return $0e112480d1414235$var$requestResponseMessage(ep, {
                type: "APPLY" /* MessageType.APPLY */ ,
                path: path.map((p)=>p.toString()),
                argumentList: argumentList
            }, transferables).then($0e112480d1414235$var$fromWireValue);
        },
        construct (_target, rawArgumentList) {
            $0e112480d1414235$var$throwIfProxyReleased(isProxyReleased);
            const [argumentList, transferables] = $0e112480d1414235$var$processArguments(rawArgumentList);
            return $0e112480d1414235$var$requestResponseMessage(ep, {
                type: "CONSTRUCT" /* MessageType.CONSTRUCT */ ,
                path: path.map((p)=>p.toString()),
                argumentList: argumentList
            }, transferables).then($0e112480d1414235$var$fromWireValue);
        }
    });
    $0e112480d1414235$var$registerProxy(proxy, ep);
    return proxy;
}
function $0e112480d1414235$var$myFlat(arr) {
    return Array.prototype.concat.apply([], arr);
}
function $0e112480d1414235$var$processArguments(argumentList) {
    const processed = argumentList.map($0e112480d1414235$var$toWireValue);
    return [
        processed.map((v)=>v[0]),
        $0e112480d1414235$var$myFlat(processed.map((v)=>v[1]))
    ];
}
const $0e112480d1414235$var$transferCache = new WeakMap();
function $0e112480d1414235$export$c9b234447cf4ef7c(obj, transfers) {
    $0e112480d1414235$var$transferCache.set(obj, transfers);
    return obj;
}
function $0e112480d1414235$export$923f96dd5afa9ce6(obj) {
    return Object.assign(obj, {
        [$0e112480d1414235$export$be5234c0b764b6e0]: true
    });
}
function $0e112480d1414235$export$f2c03fb5a6c19546(w, context = globalThis, targetOrigin = "*") {
    return {
        postMessage: (msg, transferables)=>w.postMessage(msg, targetOrigin, transferables),
        addEventListener: context.addEventListener.bind(context),
        removeEventListener: context.removeEventListener.bind(context)
    };
}
function $0e112480d1414235$var$toWireValue(value) {
    for (const [name, handler] of $0e112480d1414235$export$ab8f1c00731ee83e)if (handler.canHandle(value)) {
        const [serializedValue, transferables] = handler.serialize(value);
        return [
            {
                type: "HANDLER" /* WireValueType.HANDLER */ ,
                name: name,
                value: serializedValue
            },
            transferables
        ];
    }
    return [
        {
            type: "RAW" /* WireValueType.RAW */ ,
            value: value
        },
        $0e112480d1414235$var$transferCache.get(value) || []
    ];
}
function $0e112480d1414235$var$fromWireValue(value) {
    switch(value.type){
        case "HANDLER" /* WireValueType.HANDLER */ :
            return $0e112480d1414235$export$ab8f1c00731ee83e.get(value.name).deserialize(value.value);
        case "RAW" /* WireValueType.RAW */ :
            return value.value;
    }
}
function $0e112480d1414235$var$requestResponseMessage(ep, msg, transfers) {
    return new Promise((resolve)=>{
        const id = $0e112480d1414235$var$generateUUID();
        ep.addEventListener("message", function l(ev) {
            if (!ev.data || !ev.data.id || ev.data.id !== id) return;
            ep.removeEventListener("message", l);
            resolve(ev.data);
        });
        if (ep.start) ep.start();
        ep.postMessage(Object.assign({
            id: id
        }, msg), transfers);
    });
}
function $0e112480d1414235$var$generateUUID() {
    return new Array(4).fill(0).map(()=>Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(16)).join("-");
}


window.addEventListener("DOMContentLoaded", ()=>{
    $b63be9b0371e788c$var$websdkready();
});
function $b63be9b0371e788c$var$websdkready() {
    ZoomMtg.preLoadWasm();
    ZoomMtg.prepareWebSDK();
    console.log("[UXT] Web SDK ready and waiting for event.");
}
function $b63be9b0371e788c$var$init(leaveUrl, webEndpoint) {
    console.log("[UXT] running init");
    return new Promise((resolve, reject)=>{
        ZoomMtg.init({
            leaveUrl: leaveUrl,
            webEndpoint: webEndpoint,
            disableCORP: !window.crossOriginIsolated,
            disablePreview: true,
            debug: false,
            audioPanelAlwaysOpen: true,
            disableRecord: true,
            disableInvite: true,
            meetingInfo: [],
            showMeetingHeader: false,
            disableReport: true,
            screenShare: true,
            success: ()=>resolve(true),
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                })
        });
    });
}
function $b63be9b0371e788c$var$join(meetingConfig) {
    return new Promise((resolve, reject)=>{
        const joinPayload = {
            meetingNumber: meetingConfig.meetingNumber,
            passWord: String(meetingConfig.password),
            sdkKey: meetingConfig.sdkKey,
            signature: meetingConfig.signature,
            userName: meetingConfig.userName
        };
        if (meetingConfig.userEmail) joinPayload.userEmail = meetingConfig.userEmail;
        if (meetingConfig.tk) joinPayload.tk = meetingConfig.tk;
        if (meetingConfig.zak) joinPayload.zak = meetingConfig.zak;
        if (meetingConfig.customerKey) joinPayload.customerKey = meetingConfig.customerKey;
        ZoomMtg.join({
            ...joinPayload,
            success: ()=>resolve(true),
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                })
        });
    });
}
// ---------------------------------------------------------------------------------------------------------------------
async function $b63be9b0371e788c$var$initAndJoin(meetingConfig, serviceListener = null) {
    await $b63be9b0371e788c$var$init(meetingConfig.leaveUrl, meetingConfig.webEndpoint);
    await $b63be9b0371e788c$var$join(meetingConfig);
    await $b63be9b0371e788c$var$customizeUi();
    const observer = $b63be9b0371e788c$var$suppressLiveStreamModal();
    if (serviceListener) for (const type of [
        "onUserJoin",
        "onUserLeave",
        "onUserUpdate"
    ]){
        const cb = type === "onUserUpdate" ? $b63be9b0371e788c$var$debounce((data)=>serviceListener(type, data), 400) : (data)=>serviceListener(type, data);
        ZoomMtg.inMeetingServiceListener(type, cb);
    }
    /** API to control the Zoom player will become available only if the initialization and subsequent join is successful */ return $0e112480d1414235$export$923f96dd5afa9ce6({
        claimHostWithHostKey: $b63be9b0371e788c$var$claimHostWithHostKey,
        reclaimHost: $b63be9b0371e788c$var$reclaimHost,
        makeHost: $b63be9b0371e788c$var$makeHost,
        makeCoHost: $b63be9b0371e788c$var$makeCoHost,
        getCurrentUser: $b63be9b0371e788c$var$getCurrentUser,
        getAttendeesList: $b63be9b0371e788c$var$getAttendeesList,
        leaveMeeting: $b63be9b0371e788c$var$leaveMeeting,
        rename: $b63be9b0371e788c$var$rename,
        sendChat: $b63be9b0371e788c$var$sendChat,
        [$0e112480d1414235$export$ddc31dfe7c269837]: ()=>{
            observer.disconnect();
        }
    });
}
$0e112480d1414235$export$9b7f6e342a8cbd38($b63be9b0371e788c$var$initAndJoin, $0e112480d1414235$export$f2c03fb5a6c19546(self.parent), "https://live-interview.test.uxtweak.net".split(","));
// ---------------------------------------------------------------------------------------------------------------------
async function $b63be9b0371e788c$var$customizeUi() {
    await $b63be9b0371e788c$var$wait(500);
    /** Close "Join audio by computer popup - cannot disable it programmatically" */ const closeAudioBtn = document.querySelector(".zmu-tabs__tab-container--top button.zm-btn.join-dialog__close");
    if (closeAudioBtn) closeAudioBtn.click();
    const meetingInfoContainer = document.querySelector(".meeting-info-container");
    if (meetingInfoContainer) meetingInfoContainer.remove();
    /** Remove zoom close button since we need to use our custom */ const leaveBtnContainer = document.querySelector(".footer__leave-btn-container");
    if (leaveBtnContainer) leaveBtnContainer.remove();
    /**
   * The following buttons are disabled only via CSS, because removing them from DOM will break ZOOm react in some cases
   * - .footer__btns-container div[feature-type="record"]
   * - .footer__btns-container div[feature-type="reaction"]
   * - .footer__btns-container div[feature-type="more"]
   */ document.body.classList.remove("uxtweak-zoom-loading");
}
/** Watches for "live stream" modal being opened and closes it immediately */ function $b63be9b0371e788c$var$suppressLiveStreamModal() {
    const cb = ()=>{
        /* New MIUI modal */ const modalText = document.querySelector('#notificationManager #zoom2modal .disclaimer-content .disclaimer-content__title[title*="This meeting is being livestreamed."]');
        if (modalText && modalText.parentElement && modalText.parentElement.nextSibling && modalText.parentElement.nextSibling.childNodes[0]) modalText.parentElement.nextSibling.childNodes[0].click();
        /* Legacy modal - kept for back compatibility (remove after a year) */ /** @legacy */ const modal = document.querySelector('.ReactModalPortal .ReactModal__Content[aria-label*="livestream"]');
        if (modal) {
            const closeBtn = modal.querySelector(".zm-modal-footer-default-actions .zm-btn--primary");
            if (closeBtn) closeBtn.click();
        }
    };
    const observer = new MutationObserver(cb);
    observer.observe(document.body, {
        childList: true,
        subtree: false,
        attributes: false
    });
    /* Call for the first time, cause modal is probably already rendered. */ cb();
    return observer;
}
function $b63be9b0371e788c$var$reclaimHost() {
    return new Promise((resolve, reject)=>{
        ZoomMtg.reclaimHost({
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: ()=>resolve(true)
        });
    });
}
/**
 * Turns the current user into the meeting host.
 * If the operation is successful, `true` is returned.
 * If not, `Error` instance is thrown.
 * Custom function is needed because `ZoomMtg.claimHostWithHostKey` does  not work.
 * @param {string} hostKey
 * @return {Promise<boolean>}
 */ async function $b63be9b0371e788c$var$claimHostWithHostKey(hostKey) {
    /** Check if the current user already is a host and if so, don't do anything */ const me = await $b63be9b0371e788c$var$getCurrentUser();
    if (me.isHost) return null;
    $b63be9b0371e788c$var$disableMeetingControls();
    let errorMessage = null;
    try {
        const participantButton = document.querySelector('.footer__btns-container > div[feature-type="participants"] button');
        participantButton.click();
        await $b63be9b0371e788c$var$wait();
        const claimHostButton = document.querySelector(".participants-section-container__participants-footer-bottom button:first-child");
        /** Check if the button we found has a 'host' substring in it */ if (!claimHostButton.innerText.toLowerCase().includes("host")) {
            participantButton.click();
            throw new Error("Claim host trigger not found!");
        }
        claimHostButton.click();
        participantButton.click();
        await $b63be9b0371e788c$var$wait();
        const input = document.querySelector(".dialog-claimhost-container .zm-modal-body-content input");
        $b63be9b0371e788c$var$triggerReactInputChange(input, hostKey);
        const submitButton = document.querySelector(".dialog-claimhost-container .zm-modal-footer .zm-btn--primary");
        submitButton.click();
        await $b63be9b0371e788c$var$wait(1500);
    } catch (err) {
        errorMessage = err.message;
    }
    $b63be9b0371e788c$var$enableMeetingControls();
    if (errorMessage) throw new Error(errorMessage);
    return true;
}
function $b63be9b0371e788c$var$makeHost(userId) {
    return new Promise((resolve, reject)=>{
        ZoomMtg.makeHost({
            userId: userId,
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: ()=>resolve(true)
        });
    });
}
function $b63be9b0371e788c$var$makeCoHost(userId) {
    return new Promise((resolve, reject)=>{
        ZoomMtg.makeCoHost({
            userId: userId,
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: ()=>resolve(true)
        });
    });
}
function $b63be9b0371e788c$var$getCurrentUser() {
    return new Promise((resolve, reject)=>{
        ZoomMtg.getCurrentUser({
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: (resp)=>resolve(resp.result.currentUser)
        });
    });
}
function $b63be9b0371e788c$var$getAttendeesList() {
    return new Promise((resolve, reject)=>{
        ZoomMtg.getAttendeeslist({
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: (resp)=>resolve(resp.result.attendeesList)
        });
    });
}
function $b63be9b0371e788c$var$leaveMeeting() {
    return new Promise((resolve, reject)=>{
        ZoomMtg.leaveMeeting({
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: ()=>resolve(true)
        });
    });
}
function $b63be9b0371e788c$var$rename(userId, oldName, newName) {
    return new Promise((resolve, reject)=>{
        ZoomMtg.rename({
            newName: newName,
            oldName: oldName,
            userId: userId,
            error: (err)=>reject({
                    zoomSdkError: true,
                    ...err
                }),
            success: ()=>resolve(true)
        });
    });
}
/**
 * Custom method for sending chat messages to other users. This API has no built-in one.
 * @return {Promise<boolean>}
 */ async function $b63be9b0371e788c$var$sendChat(zoomUserId, message) {
    let chatWasClosed = false;
    try {
        /** Get the list of all users and find the target one by his zoom user ID */ const users = await $b63be9b0371e788c$var$getAttendeesList();
        const targetUser = users.find((user)=>user.userId === zoomUserId);
        if (!targetUser) throw new Error("Target user not found!");
        /** Save his old name and create a temporary "unique" name so the user can be "reliably" identified */ const oldName = targetUser.userName;
        let identifier = oldName;
        /** Check if there is at least one other user with the same name as our target user */ const renamingNeeded = users.some((user)=>user.userId !== targetUser.userId && user.userName === targetUser.userName);
        /** If such user is found, the target user must be renamed, so he has a unique name */ if (renamingNeeded) {
            identifier = `${oldName} (sending message...)`;
            await $b63be9b0371e788c$var$rename({
                userId: targetUser.userId,
                oldName: oldName,
                newName: identifier
            });
        }
        let chatReceiverMenuElement = document.getElementById("chatReceiverMenu");
        const zoomChatBtn = document.querySelector(".footer-chat-button button");
        if (!chatReceiverMenuElement) {
            $b63be9b0371e788c$var$disableMeetingControls();
            chatWasClosed = true;
            /** Simulate clicking "Chat" button */ zoomChatBtn.click();
            await $b63be9b0371e788c$var$wait(50);
            chatReceiverMenuElement = document.getElementById("chatReceiverMenu");
        }
        /* Open only if it's not already opened. */ if (!chatReceiverMenuElement.classList.contains("show")) {
            /* This may seem odd, but due great work from the Zoom team, it's necessary. Good job. */ const parent = document.defaultView.parent;
            document.defaultView.parent = null;
            chatReceiverMenuElement.firstChild.click();
            await $b63be9b0371e788c$var$wait(0);
            document.defaultView.parent = parent;
            await $b63be9b0371e788c$var$wait(0);
        }
        /** A list of all users will appear, so we query it */ const userOptionList = [
            ...chatReceiverMenuElement.querySelectorAll(".chat-receiver-list__label")
        ];
        /** Get the element that belongs to our user. Get it using the generated identifier from before. */ const targetUserOption = userOptionList.find((elm)=>elm.textContent.trim() === identifier);
        /** Simulate clicking the element that corresponds to our user. This will set the chat to direct message mode. */ targetUserOption.click();
        await $b63be9b0371e788c$var$wait(0);
        /** Query the textarea, populate it with the message, simulate React change event and simulate Enter press */ const proseMirrorElement = document.querySelector(".chat-rtf-box__editor-outer .ProseMirror");
        const dataTransfer = new DataTransfer();
        dataTransfer.setData("text/plain", message);
        proseMirrorElement.dispatchEvent(new ClipboardEvent("paste", {
            bubbles: true,
            cancelable: true,
            clipboardData: dataTransfer
        }));
        await $b63be9b0371e788c$var$wait(0);
        document.querySelector(".chat-rtf-box__send").click();
        /** If the user was renamed, we rename him back, but need to wait for 5s, since the method has a rate limit */ if (renamingNeeded) $b63be9b0371e788c$var$wait(5000).then(()=>$b63be9b0371e788c$var$rename({
                oldName: identifier,
                newName: oldName,
                userId: zoomUserId
            }));
        /** Close the chat */ if (chatWasClosed) zoomChatBtn.click();
        await $b63be9b0371e788c$var$wait(1000);
        return true;
    } finally{
        if (chatWasClosed) $b63be9b0371e788c$var$enableMeetingControls();
    }
}
// ---------------------------------------------------------------------------------------------------------------------
function $b63be9b0371e788c$var$wait(timeout = 100) {
    return new Promise((resolve)=>{
        setTimeout(()=>{
            resolve();
        }, timeout);
    });
}
function $b63be9b0371e788c$var$disableMeetingControls() {
    document.body.classList.add("uxt-disabled");
}
function $b63be9b0371e788c$var$enableMeetingControls() {
    document.body.classList.remove("uxt-disabled");
}
/**
 * @see https://stackoverflow.com/questions/39065010/why-react-event-handler-is-not-called-on-dispatchevent
 * @see https://github.com/vitalyq/react-trigger-change
 */ function $b63be9b0371e788c$var$triggerReactInputChange(node, inputValue) {
    const descriptor = Object.getOwnPropertyDescriptor(node, "value");
    node.value = `${inputValue}#`;
    if (descriptor && descriptor.configurable) delete node.value;
    node.value = inputValue;
    // TODO: Replace deprecated API with the current one
    const e = document.createEvent("HTMLEvents");
    e.initEvent("change", true, false);
    node.dispatchEvent(e);
    if (descriptor) Object.defineProperty(node, "value", descriptor);
}
/**
 * @see https://github.com/quasarframework/quasar/blob/c8c852b6a67f0bea14abc2080ac0e6bd21ebb5a0/ui/src/utils/debounce.js#L4
 */ function $b63be9b0371e788c$var$debounce(fn, wait = 250, immediate) {
    let timer = null;
    function debounced() {
        const args = arguments;
        const later = ()=>{
            timer = null;
            if (immediate !== true) fn.apply(this, args);
        };
        if (timer !== null) clearTimeout(timer);
        else if (immediate === true) fn.apply(this, args);
        timer = setTimeout(later, wait);
    }
    debounced.cancel = ()=>{
        timer !== null && clearTimeout(timer);
    };
    return debounced;
}


//# sourceMappingURL=index.aab13d7d.js.map
